import React, { useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faHandshake,
    faUsers,
    faLightbulb,
    faScaleBalanced,
    faGavel,
    faClock,
    faDollarSign,
    faShieldAlt,
    faGlobe,
    faBuilding,
    faChartLine,
    faUserTie,
    faCertificate,
    faAward,
    faCheckCircle,
    faChevronRight,
    faQuoteLeft,
    faStar,
    faBalanceScale,
    faFileContract,
    faLandmark,
    faGraduationCap,
    faPhone,
    faEnvelope,
    faCalendarAlt,
    faMapMarkerAlt,
    faRocket
} from "@fortawesome/free-solid-svg-icons";
import { Cta } from "../components/Cta";

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
        }
    }
};

const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: "easeOut" }
    }
};

const adrMethods = [
    {
        id: "arbitration",
        title: "Arbitration",
        icon: faGavel,
        color: "gold",
        description: "A private dispute resolution process where parties submit their dispute to one or more arbitrators who make a binding decision.",
        advantages: [
            "Legally Binding Awards",
            "Confidential Proceedings",
            "Expert Arbitrators",
            "Faster Resolution",
            "Enforceable Internationally"
        ],
        bestFor: [
            "Commercial Disputes",
            "Construction Contracts",
            "International Contracts",
            "Technical Matters",
            "Confidential Disputes"
        ],
        process: [
            "Arbitration Agreement",
            "Appointment of Arbitrator",
            "Statement of Claim/Defense",
            "Hearings & Evidence",
            "Arbitral Award"
        ],
        duration: "3-12 months",
        cost: "Medium to High",
        enforceability: "High "
    },
    {
        id: "mediation",
        title: "Mediation",
        icon: faUsers,
        color: "primary-blue",
        description: "A voluntary process where a neutral third party facilitates communication between disputing parties to help them reach a mutually acceptable agreement.",
        advantages: [
            "Party Control Over Outcome",
            "Preserves Relationships",
            "Cost Effective",
            "Creative Solutions",
            "Quick Resolution"
        ],
        bestFor: [
            "Business Partnerships",
            "Family Business Disputes",
            "Neighbor Conflicts",
            "Employment Disputes",
            "Consumer Disputes"
        ],
        process: [
            "Joint Mediation Session",
            "Private Caucuses",
            "Issue Identification",
            "Option Generation",
            "Settlement Agreement"
        ],
        duration: "1-3 days",
        cost: "Low to Medium",
        enforceability: "Medium (Contract Based)"
    },
    {
        id: "conciliation",
        title: "Conciliation",
        icon: faHandshake,
        color: "gold",
        description: "Similar to mediation but the conciliator plays a more active role in proposing terms of settlement and helping parties reach agreement.",
        advantages: [
            "Proactive Conciliator Role",
            "Settlement Proposals",
            "Formal Yet Flexible",
            "Statutory Recognition",
            "Industrial Dispute Focus"
        ],
        bestFor: [
            "Industrial Disputes",
            "Labor Conflicts",
            "Commercial Disagreements",
            "Government Contracts",
            "Community Disputes"
        ],
        process: [
            "Conciliation Request",
            "Conciliator Appointment",
            "Fact Finding",
            "Settlement Proposals",
            "Conciliation Settlement"
        ],
        duration: "1-4 weeks",
        cost: "Low",
        enforceability: "Medium (Statutory)"
    }
];

const arbitrationTypes = [
    {
        type: "Domestic Arbitration",
        description: "Arbitration conducted within India under Indian law",
        icon: faBuilding,
        features: ["Indian Law Applicable", "Indian Arbitrators", "Indian Courts Jurisdiction"],
        color: "gold"
    },
    {
        type: "International Arbitration",
        description: "Cross-border disputes involving foreign parties or laws",
        icon: faGlobe,
        features: ["UNCITRAL Rules", "Foreign Law Applicable", "Enforceable Worldwide"],
        color: "primary-blue"
    },
    {
        type: "Institutional Arbitration",
        description: "Administered by established arbitral institutions",
        icon: faLandmark,
        features: ["ICCA Rules", "LCIA Rules", "SIAC Rules", "ICC Rules"],
        color: "gold"
    },
    {
        type: "Ad-hoc Arbitration",
        description: "Parties determine their own procedure",
        icon: faFileContract,
        features: ["Flexible Procedure", "Party Control", "Cost Effective"],
        color: "primary-blue"
    }
];

const sectors = [
    {
        sector: "Construction & Infrastructure",
        cases: ["Delay Claims", "Defect Liability", "Payment Disputes", "Contract Termination"],
        icon: faBuilding,
        color: "gold"
    },
    {
        sector: "Corporate & Commercial",
        cases: ["Shareholder Disputes", "Joint Venture Conflicts", "M&A Disputes", "Franchise Agreements"],
        icon: faChartLine,
        color: "primary-blue"
    },
    {
        sector: "International Trade",
        cases: ["Export-Import Disputes", "Shipping Contracts", "Quality Disputes", "Payment Defaults"],
        icon: faGlobe,
        color: "gold"
    },
    {
        sector: "Intellectual Property",
        cases: ["Patent Infringement", "Trademark Conflicts", "Licensing Disputes", "Royalty Payments"],
        icon: faCertificate,
        color: "primary-blue"
    }
];

const successCases = [
    {
        title: "₹250 Cr Construction Arbitration",
        parties: "InfraCorp vs. State Government",
        method: "Arbitration",
        result: "Award in favor of InfraCorp",
        duration: "8 months",
        value: "₹250 Crores"
    },
    {
        title: "Family Business Mediation",
        parties: "Family Shareholders Dispute",
        method: "Mediation",
        result: "Amicable Settlement",
        duration: "2 days",
        value: "Business Preserved"
    },
    {
        title: "International Trade Conciliation",
        parties: "Indian Exporter vs. European Buyer",
        method: "Conciliation",
        result: "Mutual Agreement",
        duration: "3 weeks",
        value: "$5 Million Recovery"
    },
    {
        title: "Joint Venture Arbitration",
        parties: "Tech Company vs. Foreign Partner",
        method: "Arbitration",
        result: "Favorable Settlement",
        duration: "6 months",
        value: "Partnership Continued"
    }
];

const comparativeTable = [
    {
        aspect: "Decision Maker",
        arbitration: "Arbitrator(s)",
        mediation: "Parties Themselves",
        conciliation: "Parties with Conciliator Help"
    },
    {
        aspect: "Binding Nature",
        arbitration: "Binding Award",
        mediation: "Voluntary Agreement",
        conciliation: "Settlement Agreement"
    },
    {
        aspect: "Confidentiality",
        arbitration: "High",
        mediation: "Very High",
        conciliation: "High"
    },
    {
        aspect: "Time Required",
        arbitration: "3-12 months",
        mediation: "1-3 days",
        conciliation: "1-4 weeks"
    },
    {
        aspect: "Cost",
        arbitration: "Medium-High",
        mediation: "Low-Medium",
        conciliation: "Low"
    },
    {
        aspect: "Formality",
        arbitration: "Formal",
        mediation: "Informal",
        conciliation: "Semi-Formal"
    }
];

export const ADRServices = () => {
    const [activeMethod, setActiveMethod] = useState(0);
    const [hoveredSector, setHoveredSector] = useState(null);

    return (
        <div style={{
            backgroundColor: "var(--cream)",
            minHeight: "100vh",
            padding: "130px 0 60px",
            position: "relative"
        }}>
            {/* Background decorative elements */}
            <div className="position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 0, pointerEvents: "none", overflow: "hidden" }}>
                <div className="position-absolute top-0 end-0 w-25 h-25"
                    style={{
                        background: "radial-gradient(circle, var(--gold) 0%, transparent 70%)",
                        opacity: 0.03,
                        transform: "translate(30%, -30%)"
                    }}
                ></div>
                <div className="position-absolute bottom-0 start-0 w-30 h-30"
                    style={{
                        background: "radial-gradient(circle, var(--primary-blue) 0%, transparent 70%)",
                        opacity: 0.03,
                        transform: "translate(-30%, 30%)"
                    }}
                ></div>
            </div>

            <div className="container position-relative" style={{ zIndex: 1 }}>
                {/* Hero Section */}
                <motion.div
                    className="text-center mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <motion.div variants={fadeInUp}>
                        <span className="d-inline-block px-4 py-2 rounded-pill mb-3"
                            style={{
                                color: "var(--gold)",
                                fontWeight: "600",
                                letterSpacing: "2px",
                                fontSize: "0.9rem",
                                textTransform: "uppercase",
                                border: "1px solid var(--gold-light)",
                                background: "rgba(184, 134, 11, 0.05)",
                                backdropFilter: "blur(4px)"
                            }}
                        >
                            Alternative Dispute Resolution
                        </span>


                        <motion.h4
                            className="fw-bold display-6 display-md-5 mt-2"
                            variants={fadeInUp}
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                fontWeight: 600,
                                color: 'var(--primary-dark)',
                                background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--gold) 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text"
                            }}
                        >
                            Arbitration, Mediation & Conciliation Services
                        </motion.h4>
                    </motion.div>

                    <motion.p
                        className="fs-6 fs-md-5 mx-auto mb-3 mb-md-4"
                        variants={fadeInUp}
                        style={{
                            maxWidth: "750px",
                            color: "var(--text-primary)",
                            lineHeight: 1.7
                        }}
                    >
                        Expert guidance in Alternative Dispute Resolution (ADR) methods that offer faster,
                        more cost-effective, and confidential alternatives to traditional court litigation.
                    </motion.p>
                </motion.div>

                {/* ADR Methods Overview */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faHandshake} className="me-3" style={{ color: "var(--gold)" }} />
                            Alternative Dispute Resolution Methods
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Choose the right ADR method for your specific dispute resolution needs
                        </p>
                    </div>

                    {/* Method Selection Tabs */}
                    <div className="d-flex flex-wrap justify-content-center gap-3 mb-4">
                        {adrMethods.map((method, index) => (
                            <motion.button
                                key={method.id}
                                className={`btn px-4 py-3 rounded-pill d-flex align-items-center gap-3 ${activeMethod === index ? 'active' : ''}`}
                                onClick={() => setActiveMethod(index)}
                                variants={itemVariants}
                                style={{
                                    backgroundColor: activeMethod === index ? `var(--${method.color})` : "transparent",
                                    color: activeMethod === index ? "var(--white)" : `var(--${method.color})`,
                                    border: `2px solid var(--${method.color})`,
                                    fontWeight: "600",
                                    transition: "var(--transition)",
                                    minWidth: "220px"
                                }}
                                whileHover={{
                                    scale: 1.05,
                                    backgroundColor: activeMethod === index ? `var(--${method.color}-light)` : `rgba(var(--${method.color}-rgb), 0.1)`
                                }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <FontAwesomeIcon icon={method.icon} />
                                <span>{method.title}</span>
                            </motion.button>
                        ))}
                    </div>

                    {/* Active Method Details */}
                    <motion.div
                        className="rounded-4 overflow-hidden"
                        style={{
                            backgroundColor: "var(--white)",
                            border: `2px solid var(--${adrMethods[activeMethod].color})`,
                            boxShadow: "var(--shadow-medium)"
                        }}
                        key={activeMethod}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="p-4 p-md-5">
                            <div className="row g-4">
                                {/* Left Column - Overview */}
                                <div className="col-lg-8">
                                    <div className="d-flex align-items-start gap-4 mb-4">
                                        <motion.div
                                            className="p-3 rounded-3 flex-shrink-0"
                                            style={{
                                                backgroundColor: `rgba(var(--${adrMethods[activeMethod].color}-rgb), 0.1)`,
                                                color: `var(--${adrMethods[activeMethod].color})`
                                            }}
                                            whileHover={{ rotate: 360 }}
                                            transition={{ duration: 0.6, ease: "easeInOut" }}
                                        >
                                            <FontAwesomeIcon icon={adrMethods[activeMethod].icon} style={{ fontSize: "2rem" }} />
                                        </motion.div>
                                        <div>
                                            <h3 className="fw-bold mb-2" style={{ color: "var(--primary-dark)" }}>
                                                {adrMethods[activeMethod].title}
                                            </h3>
                                            <p className="mb-0" style={{ color: "var(--text-primary)", lineHeight: 1.7 }}>
                                                {adrMethods[activeMethod].description}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="row g-4">
                                        {/* Advantages */}
                                        <div className="col-md-6">
                                            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2"
                                                style={{ color: "var(--primary-dark)" }}
                                            >
                                                <FontAwesomeIcon icon={faStar} style={{ color: `var(--${adrMethods[activeMethod].color})` }} />
                                                Key Advantages
                                            </h6>
                                            <div className="vstack gap-2">
                                                {adrMethods[activeMethod].advantages.map((advantage, idx) => (
                                                    <motion.div
                                                        key={idx}
                                                        className="d-flex align-items-center gap-2 p-2 rounded-3"
                                                        style={{
                                                            backgroundColor: "rgba(var(--gold-rgb), 0.03)",
                                                            border: "1px solid var(--gold-light)"
                                                        }}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        whileInView={{ opacity: 1, x: 0 }}
                                                        viewport={{ once: true }}
                                                        transition={{ delay: idx * 0.1 }}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faCheckCircle}
                                                            style={{
                                                                color: `var(--${adrMethods[activeMethod].color})`,
                                                                fontSize: "0.8rem"
                                                            }}
                                                        />
                                                        <span className="small" style={{ color: "var(--text-primary)" }}>
                                                            {advantage}
                                                        </span>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Best For */}
                                        <div className="col-md-6">
                                            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2"
                                                style={{ color: "var(--primary-dark)" }}
                                            >
                                                <FontAwesomeIcon icon={faLightbulb} style={{ color: `var(--${adrMethods[activeMethod].color})` }} />
                                                Best Suited For
                                            </h6>
                                            <div className="vstack gap-2">
                                                {adrMethods[activeMethod].bestFor.map((suit, idx) => (
                                                    <motion.div
                                                        key={idx}
                                                        className="d-flex align-items-center gap-2 p-2 rounded-3"
                                                        style={{
                                                            backgroundColor: "rgba(var(--primary-blue-rgb), 0.03)",
                                                            border: "1px solid var(--primary-blue)"
                                                        }}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        whileInView={{ opacity: 1, x: 0 }}
                                                        viewport={{ once: true }}
                                                        transition={{ delay: idx * 0.1 }}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faChevronRight}
                                                            style={{
                                                                color: `var(--${adrMethods[activeMethod].color})`,
                                                                fontSize: "0.7rem"
                                                            }}
                                                        />
                                                        <span className="small" style={{ color: "var(--text-primary)" }}>
                                                            {suit}
                                                        </span>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Column - Process & Stats */}
                                <div className="col-lg-4">
                                    <div className="p-4 rounded-4 h-100"
                                        style={{
                                            backgroundColor: `rgba(var(--${adrMethods[activeMethod].color}-rgb), 0.05)`,
                                            border: `1px solid var(--${adrMethods[activeMethod].color}-light)`
                                        }}
                                    >
                                        <h6 className="fw-bold mb-3" style={{ color: `var(--${adrMethods[activeMethod].color})` }}>
                                            Process & Timeline
                                        </h6>

                                        <div className="vstack gap-3 mb-4">
                                            {adrMethods[activeMethod].process.map((step, idx) => (
                                                <div key={idx} className="d-flex align-items-start gap-2">
                                                    <div className="p-1 rounded-circle flex-shrink-0 mt-1"
                                                        style={{
                                                            backgroundColor: `rgba(var(--${adrMethods[activeMethod].color}-rgb), 0.2)`,
                                                            color: `var(--${adrMethods[activeMethod].color})`,
                                                            width: "20px",
                                                            height: "20px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontSize: "0.7rem",
                                                            fontWeight: "bold"
                                                        }}
                                                    >
                                                        {idx + 1}
                                                    </div>
                                                    <span className="small" style={{ color: "var(--text-primary)" }}>
                                                        {step}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Stats */}
                                        <div className="vstack gap-3">
                                            <div className="d-flex justify-content-between align-items-center p-2 border-bottom"
                                                style={{ borderColor: "var(--gold-light)" }}
                                            >
                                                <span className="small" style={{ color: "var(--text-primary)" }}>Duration</span>
                                                <span className="fw-bold" style={{ color: `var(--${adrMethods[activeMethod].color})` }}>
                                                    {adrMethods[activeMethod].duration}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center p-2 border-bottom"
                                                style={{ borderColor: "var(--gold-light)" }}
                                            >
                                                <span className="small" style={{ color: "var(--text-primary)" }}>Cost</span>
                                                <span className="fw-bold" style={{ color: `var(--${adrMethods[activeMethod].color})` }}>
                                                    {adrMethods[activeMethod].cost}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center p-2"
                                                style={{ borderColor: "var(--gold-light)" }}
                                            >
                                                <span className="small" style={{ color: "var(--text-primary)" }}>Enforceability</span>
                                                <span className="fw-bold" style={{ color: `var(--${adrMethods[activeMethod].color})` }}>
                                                    {adrMethods[activeMethod].enforceability}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>

                {/* Arbitration Types */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faGavel} className="me-3" style={{ color: "var(--gold)" }} />
                            Types of Arbitration
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Specialized arbitration services for different legal contexts
                        </p>
                    </div>

                    <div className="row g-4">
                        {arbitrationTypes.map((type, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100 text-center"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `2px solid var(--${type.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -8,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: `var(--${type.color})`
                                    }}
                                >
                                    <motion.div
                                        className="mb-3"
                                        whileHover={{ scale: 1.1, rotate: 5 }}
                                    >
                                        <div className="p-3 rounded-circle d-inline-block"
                                            style={{
                                                backgroundColor: `rgba(var(--${type.color}-rgb), 0.1)`,
                                                color: `var(--${type.color})`
                                            }}
                                        >
                                            <FontAwesomeIcon icon={type.icon} style={{ fontSize: "1.5rem" }} />
                                        </div>
                                    </motion.div>

                                    <h5 className="fw-bold mb-3" style={{ color: "var(--primary-dark)" }}>
                                        {type.type}
                                    </h5>

                                    <p className="small mb-4" style={{
                                        color: "var(--text-primary)",
                                        lineHeight: 1.6,
                                        minHeight: "48px"
                                    }}>
                                        {type.description}
                                    </p>

                                    <div className="vstack gap-2">
                                        {type.features.map((feature, idx) => (
                                            <div key={idx} className="d-flex align-items-center gap-2 justify-content-center">
                                                <FontAwesomeIcon
                                                    icon={faCheckCircle}
                                                    style={{
                                                        color: `var(--${type.color})`,
                                                        fontSize: "0.7rem"
                                                    }}
                                                />
                                                <span className="small" style={{ color: "var(--text-primary)" }}>
                                                    {feature}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Sector Expertise */}
                {/* <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faBuilding} className="me-3" style={{ color: "var(--gold)" }} />
                            Sector-Specific ADR Expertise
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Industry-focused dispute resolution across key sectors
                        </p>
                    </div>

                    <div className="row g-4">
                        {sectors.map((sector, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                                onMouseEnter={() => setHoveredSector(index)}
                                onMouseLeave={() => setHoveredSector(null)}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `1px solid var(--${sector.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: `var(--${sector.color})`
                                    }}
                                >
                                    <div className="d-flex align-items-center gap-3 mb-3">
                                        <div className="p-2 rounded-3 flex-shrink-0"
                                            style={{
                                                backgroundColor: `rgba(var(--${sector.color}-rgb), 0.1)`,
                                                color: `var(--${sector.color})`
                                            }}
                                        >
                                            <FontAwesomeIcon icon={sector.icon} />
                                        </div>
                                        <h5 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                                            {sector.sector}
                                        </h5>
                                    </div>

                                    <div className="vstack gap-2">
                                        {sector.cases.map((caseType, idx) => (
                                            <motion.div
                                                key={idx}
                                                className="d-flex align-items-center gap-2 p-2 rounded-3"
                                                style={{
                                                    backgroundColor: "rgba(var(--gold-rgb), 0.03)",
                                                    border: "1px solid var(--gold-light)"
                                                }}
                                                initial={{ opacity: 0, x: -10 }}
                                                whileInView={{ opacity: 1, x: 0 }}
                                                viewport={{ once: true }}
                                                transition={{ delay: idx * 0.1 }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faScaleBalanced}
                                                    style={{
                                                        color: `var(--${sector.color})`,
                                                        fontSize: "0.7rem"
                                                    }}
                                                />
                                                <span className="small" style={{ color: "var(--text-primary)" }}>
                                                    {caseType}
                                                </span>
                                            </motion.div>
                                        ))}
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div> */}

                {/* Comparative Analysis */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faBalanceScale} className="me-3" style={{ color: "var(--gold)" }} />
                            Comparative Analysis
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Understanding the differences between ADR methods
                        </p>
                    </div>

                    <div className="rounded-4 overflow-hidden"
                        style={{
                            backgroundColor: "var(--white)",
                            border: "1px solid var(--gold-light)",
                            boxShadow: "var(--shadow-medium)"
                        }}
                    >
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead>
                                    <tr style={{ backgroundColor: "rgba(var(--gold-rgb), 0.05)" }}>
                                        <th style={{
                                            color: "var(--primary-dark)",
                                            borderBottom: "2px solid var(--gold-light)",
                                            padding: "1rem"
                                        }}>
                                            Aspect
                                        </th>
                                        <th style={{
                                            color: "var(--gold)",
                                            borderBottom: "2px solid var(--gold-light)",
                                            padding: "1rem",
                                            textAlign: "center"
                                        }}>
                                            Arbitration
                                        </th>
                                        <th style={{
                                            color: "var(--primary-blue)",
                                            borderBottom: "2px solid var(--gold-light)",
                                            padding: "1rem",
                                            textAlign: "center"
                                        }}>
                                            Mediation
                                        </th>
                                        <th style={{
                                            color: "var(--gold)",
                                            borderBottom: "2px solid var(--gold-light)",
                                            padding: "1rem",
                                            textAlign: "center"
                                        }}>
                                            Conciliation
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {comparativeTable.map((row, index) => (
                                        <tr key={index} style={{
                                            borderBottom: "1px solid var(--gold-light)",
                                            transition: "var(--transition)"
                                        }}>
                                            <td style={{
                                                color: "var(--text-primary)",
                                                padding: "1rem",
                                                fontWeight: "500"
                                            }}>
                                                {row.aspect}
                                            </td>
                                            <td style={{
                                                color: "var(--text-primary)",
                                                padding: "1rem",
                                                textAlign: "center"
                                            }}>
                                                {row.arbitration}
                                            </td>
                                            <td style={{
                                                color: "var(--text-primary)",
                                                padding: "1rem",
                                                textAlign: "center"
                                            }}>
                                                {row.mediation}
                                            </td>
                                            <td style={{
                                                color: "var(--text-primary)",
                                                padding: "1rem",
                                                textAlign: "center"
                                            }}>
                                                {row.conciliation}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </motion.div>

                {/* Success Cases */}
                {/* <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faAward} className="me-3" style={{ color: "var(--gold)" }} />
                            Successful ADR Cases
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Recent achievements in alternative dispute resolution
                        </p>
                    </div>

                    <div className="row g-4">
                        {successCases.map((caseItem, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: "1px solid var(--gold-light)",
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: "var(--gold)"
                                    }}
                                >
                                    <div className="mb-3">
                                        <span className="badge px-3 py-2 mb-2"
                                            style={{
                                                backgroundColor:
                                                    caseItem.method === "Arbitration" ? "rgba(var(--gold-rgb), 0.1)" :
                                                        caseItem.method === "Mediation" ? "rgba(var(--primary-blue-rgb), 0.1)" :
                                                            "rgba(var(--gold-rgb), 0.1)",
                                                color:
                                                    caseItem.method === "Arbitration" ? "var(--gold)" :
                                                        caseItem.method === "Mediation" ? "var(--primary-blue)" :
                                                            "var(--gold)",
                                                border: "1px solid var(--gold-light)"
                                            }}
                                        >
                                            {caseItem.method}
                                        </span>
                                        <h6 className="fw-bold mb-2" style={{ color: "var(--primary-dark)" }}>
                                            {caseItem.title}
                                        </h6>
                                        <div className="small mb-3" style={{ color: "var(--gold)" }}>
                                            {caseItem.parties}
                                        </div>
                                    </div>

                                    <div className="vstack gap-2 mb-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <FontAwesomeIcon
                                                icon={faCheckCircle}
                                                style={{
                                                    color: "var(--green-accent)",
                                                    fontSize: "0.8rem"
                                                }}
                                            />
                                            <span className="small fw-bold" style={{ color: "var(--text-primary)" }}>
                                                {caseItem.result}
                                            </span>
                                        </div>

                                        <div className="d-flex align-items-center gap-2">
                                            <FontAwesomeIcon
                                                icon={faClock}
                                                style={{
                                                    color: "var(--primary-blue)",
                                                    fontSize: "0.8rem"
                                                }}
                                            />
                                            <span className="small" style={{ color: "var(--text-primary)" }}>
                                                {caseItem.duration}
                                            </span>
                                        </div>

                                        <div className="d-flex align-items-center gap-2">
                                            <FontAwesomeIcon
                                                icon={faDollarSign}
                                                style={{
                                                    color: "var(--gold)",
                                                    fontSize: "0.8rem"
                                                }}
                                            />
                                            <span className="small fw-bold" style={{ color: "var(--text-primary)" }}>
                                                {caseItem.value}
                                            </span>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div> */}

                {/* Why Choose RNA for ADR */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="p-4 p-md-5 rounded-4"
                        style={{
                            backgroundColor: "var(--primary-dark)",
                            backgroundImage: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary-blue) 100%)",
                            border: "1px solid var(--gold-light)"
                        }}
                    >
                        <div className="row align-items-center">
                            <div className="col-lg-8">
                                <motion.div variants={fadeInUp}>
                                    <h3 className="fw-bold mb-3" style={{ color: "var(--white)" }}>
                                        <FontAwesomeIcon icon={faUserTie} className="me-3" style={{ color: "var(--gold)" }} />
                                        Why Choose RNA for ADR?
                                    </h3>

                                    <div className="row g-4 mb-4">
                                        <div className="col-md-6">
                                            <div className="d-flex align-items-start gap-3 mb-3">
                                                <FontAwesomeIcon
                                                    icon={faGraduationCap}
                                                    style={{
                                                        color: "var(--gold)",
                                                        fontSize: "1.2rem",
                                                        marginTop: "2px"
                                                    }}
                                                />
                                                <div>
                                                    <div className="fw-bold mb-1" style={{ color: "var(--white)" }}>
                                                        London-Educated Expertise
                                                    </div>
                                                    <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                        International ADR perspective
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* <div className="col-md-6">
                                            <div className="d-flex align-items-start gap-3 mb-3">
                                                <FontAwesomeIcon
                                                    icon={faCertificate}
                                                    style={{
                                                        color: "var(--gold)",
                                                        fontSize: "1.2rem",
                                                        marginTop: "2px"
                                                    }}
                                                />
                                                <div>
                                                    <div className="fw-bold mb-1" style={{ color: "var(--white)" }}>
                                                        Certified Arbitrators & Mediators
                                                    </div>
                                                    <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                        Trained in international ADR
                                                    </div>
                                                </div>
                                            </div>
                                        </div> */}

                                        <div className="col-md-6">
                                            <div className="d-flex align-items-start gap-3 mb-3">
                                                <FontAwesomeIcon
                                                    icon={faShieldAlt}
                                                    style={{
                                                        color: "var(--gold)",
                                                        fontSize: "1.2rem",
                                                        marginTop: "2px"
                                                    }}
                                                />
                                                <div>
                                                    <div className="fw-bold mb-1" style={{ color: "var(--white)" }}>
                                                        Confidential Proceedings
                                                    </div>
                                                    <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                        Protecting sensitive information
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="d-flex align-items-start gap-3 mb-3">
                                                <FontAwesomeIcon
                                                    icon={faRocket}
                                                    style={{
                                                        color: "var(--gold)",
                                                        fontSize: "1.2rem",
                                                        marginTop: "2px"
                                                    }}
                                                />
                                                <div>
                                                    <div className="fw-bold mb-1" style={{ color: "var(--white)" }}>
                                                        Faster Resolution
                                                    </div>
                                                    <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                        Compared to court litigation
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>

                            <div className="col-lg-4">
                                <motion.div
                                    className="text-center"
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.05 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                >
                                    <div className="p-4 rounded-4 d-inline-block"
                                        style={{
                                            backgroundColor: "rgba(184, 134, 11, 0.15)",
                                            border: "2px solid var(--gold)",
                                            backdropFilter: "blur(10px)"
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faHandshake}
                                            className="mb-3"
                                            style={{ fontSize: "3rem", color: "var(--gold)" }}
                                        />
                                        {/* <div className="display-6 fw-bold mb-2" style={{ color: "var(--white)" }}>
                                            95%
                                        </div> */}
                                        <div style={{ color: "var(--gold)" }}>
                                           Good ADR Success Rate
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* CTA Section */}
              

               <Cta Title="Ready for Alternative Dispute Resolution?"description="Contact our ADR experts to explore faster, more cost-effective alternatives to traditional litigation for your dispute."/>
            </div>
        </div>
    );
};